import { useToast } from '@components/index'
import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useOnError } from '@services/utils/onError' // Added import
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { FlatDetail } from 'types/api'
import { BaseParams } from 'types/utils'
import { flatsQueryKey } from '../queries/useFlats'
import { FlatWrite } from '../types'

export function useUpdateFlat() {
  const toast = useToast()
  const { t } = useTranslation('toast')
  const queryClient = useQueryClient()
  const onError = useOnError() // Added useOnError

  async function updateFlat({
    id,
    body
  }: BaseParams<[FlatWrite, FlatDetail], 'PATCH'>) {
    const formData = new FormData()
    if (body)
      Object.entries(body).forEach(([key, value]) => {
        // `undefined` means "leave untouched" and is skipped. `null` means
        // "clear this field" and is sent as an empty string - DRF turns an
        // empty multipart value into None for nullable fields.
        if (value === undefined) return
        if (value === null) {
          formData.append(key, '')
        } else if (Array.isArray(value)) {
          if (value.length > 0)
            value.forEach((item) => formData.append(key, item as string | Blob))
        } else {
          formData.append(key, value as string | Blob)
        }
      })

    const res = await apiClient.patch<FlatDetail>(
      SNA_PREFIX + `flats/${id}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    return res.data
  }

  return useMutation({
    mutationFn: updateFlat,
    onSuccess: (data, variables) => {
      variables.params?.onSuccess?.(data)
      toast.success({ message: t('flatUpdated') })
      queryClient.invalidateQueries({ queryKey: [...flatsQueryKey] })
    },
    onError: (data, variables) => {
      variables.params?.onError?.()
      onError(data)
    } // Updated to use useOnError
  })
}
